import React, { useEffect, useState } from "react";
import { http } from "@/context/AuthContext";
import {
  CheckCircle2,
  AlertCircle,
  XCircle,
  Loader2,
  ExternalLink,
  Copy,
  Check,
  Plug,
} from "lucide-react";
import { BRAND } from "@/lib/data";

const STATUS_META = {
  connected: { color: "text-teal", bg: "bg-teal/10", icon: CheckCircle2, label: "Connected" },
  awaiting_first_event: {
    color: "text-gold",
    bg: "bg-gold/15",
    icon: AlertCircle,
    label: "Waiting on first event",
  },
  partial: { color: "text-gold", bg: "bg-gold/15", icon: AlertCircle, label: "Partial" },
  not_configured: { color: "text-red-500", bg: "bg-red-50", icon: XCircle, label: "Not configured" },
};

function CopyableUrl({ url, label }) {
  const [copied, setCopied] = useState(false);
  if (!url) return null;
  return (
    <div className="mt-3 p-3 rounded-xl bg-navy/5 text-xs flex items-center gap-2 group">
      <span className="text-navy/55 uppercase tracking-widest text-[10px] flex-shrink-0">
        {label}
      </span>
      <code className="flex-1 truncate text-navy/80 font-mono">{url}</code>
      <button
        data-testid={`copy-${label.toLowerCase().replace(/\s/g, "-")}`}
        onClick={() => {
          navigator.clipboard.writeText(url);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
        className="text-navy/50 hover:text-navy"
        aria-label="Copy"
      >
        {copied ? <Check className="w-3.5 h-3.5 text-teal" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

function IntegrationCard({ integration }) {
  const meta = STATUS_META[integration.status] || STATUS_META.not_configured;
  const Icon = meta.icon;
  const apiBase = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const webhookUrl =
    integration.key === "bookipi"
      ? `${apiBase}/integrations/bookipi/webhook`
      : integration.key === "cdm"
      ? `${apiBase}/integrations/cdm/webhook`
      : null;

  return (
    <div
      data-testid={`integration-${integration.key}`}
      className="card p-6 flex flex-col"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-navy/50">
            {integration.category}
          </div>
          <h3 className="font-serif text-xl text-navy mt-1">{integration.name}</h3>
        </div>
        <div
          className={`flex items-center gap-1.5 ${meta.color} ${meta.bg} text-[11px] font-medium uppercase tracking-wider px-2.5 py-1 rounded-full whitespace-nowrap`}
        >
          <Icon className="w-3.5 h-3.5" />
          {meta.label}
        </div>
      </div>

      <p className="mt-3 text-sm text-navy/65 leading-relaxed">{integration.note}</p>

      {integration.events_received !== undefined && (
        <div className="mt-3 text-xs text-navy/55">
          <span className="font-medium text-navy/75">{integration.events_received}</span> events
          received
        </div>
      )}
      {integration.subscribers !== undefined && (
        <div className="mt-3 text-xs text-navy/55">
          <span className="font-medium text-navy/75">{integration.subscribers}</span> newsletter
          subscribers
        </div>
      )}
      {integration.leads_synced !== undefined && (
        <div className="mt-3 text-xs text-navy/55">
          <span className="font-medium text-navy/75">{integration.leads_synced}</span> leads in CRM
          pipeline
        </div>
      )}
      {integration.mode && integration.mode !== "open_endpoint" && (
        <div className="mt-2 text-xs text-navy/55">
          Mode:{" "}
          <span className="font-medium text-navy/75">
            {integration.mode.replace(/_/g, " ")}
          </span>
        </div>
      )}

      {webhookUrl && (
        <CopyableUrl url={webhookUrl} label="Webhook URL" />
      )}

      {integration.env_keys && integration.env_keys.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {integration.env_keys.map((k) => (
            <span
              key={k}
              className="text-[10px] font-mono px-2 py-0.5 rounded-md bg-navy/5 text-navy/65"
            >
              {k}
            </span>
          ))}
        </div>
      )}

      {integration.where_to_get && integration.status === "not_configured" && (
        <a
          href={integration.where_to_get.startsWith("http") ? integration.where_to_get : "#"}
          target="_blank"
          rel="noreferrer"
          className="mt-4 text-xs text-gold inline-flex items-center gap-1 hover:underline"
          data-testid={`where-${integration.key}`}
        >
          {integration.where_to_get.startsWith("http") ? (
            <>
              Get the key <ExternalLink className="w-3 h-3" />
            </>
          ) : (
            <span className="text-navy/55 no-underline">{integration.where_to_get}</span>
          )}
        </a>
      )}
    </div>
  );
}

export default function AdminIntegrations() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    http
      .get("/admin/integrations/health")
      .then((r) => setData(r.data))
      .catch((err) =>
        setError(err?.response?.data?.detail || "Unable to load integration status.")
      )
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div
        data-testid="admin-integrations-loading"
        className="flex items-center gap-2 text-navy/60"
      >
        <Loader2 className="w-4 h-4 animate-spin" /> Loading integration status…
      </div>
    );
  }
  if (error) {
    return (
      <div
        data-testid="admin-integrations-error"
        className="card p-6 text-red-500 text-sm"
      >
        {error}
      </div>
    );
  }

  const grouped = (data?.integrations || []).reduce((acc, i) => {
    acc[i.category] = acc[i.category] || [];
    acc[i.category].push(i);
    return acc;
  }, {});

  const summary = (data?.integrations || []).reduce(
    (acc, i) => {
      if (i.status === "connected") acc.connected += 1;
      else if (i.status === "not_configured") acc.missing += 1;
      else acc.partial += 1;
      return acc;
    },
    { connected: 0, partial: 0, missing: 0 }
  );

  return (
    <div data-testid="admin-integrations-page" className="space-y-8">
      <div>
        <div className="flex items-center gap-3">
          <Plug className="w-6 h-6 text-gold" />
          <h1 className="font-serif text-4xl text-navy">Integration Health</h1>
        </div>
        <p className="mt-3 text-navy/65 max-w-2xl leading-relaxed">
          A live status check of every third-party service powering ConnectHER.
          Use this page to see what's connected, what's missing keys, and where
          to grab those keys when you're ready.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="card p-5 text-center" data-testid="summary-connected">
          <div className="font-serif text-4xl text-teal">{summary.connected}</div>
          <div className="text-xs uppercase tracking-widest text-navy/55 mt-1">
            Connected
          </div>
        </div>
        <div className="card p-5 text-center" data-testid="summary-partial">
          <div className="font-serif text-4xl text-gold">{summary.partial}</div>
          <div className="text-xs uppercase tracking-widest text-navy/55 mt-1">
            Awaiting
          </div>
        </div>
        <div className="card p-5 text-center" data-testid="summary-missing">
          <div className="font-serif text-4xl text-red-500">{summary.missing}</div>
          <div className="text-xs uppercase tracking-widest text-navy/55 mt-1">
            Missing keys
          </div>
        </div>
      </div>

      {Object.entries(grouped).map(([category, items]) => (
        <div key={category}>
          <h2 className="text-xs uppercase tracking-[0.25em] text-navy/55 mb-4">
            {category}
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {items.map((i) => (
              <IntegrationCard key={i.key} integration={i} />
            ))}
          </div>
        </div>
      ))}

      <div className="card p-6 bg-navy/5 border-navy/10">
        <h3 className="font-serif text-xl text-navy">Quick links</h3>
        <ul className="mt-3 space-y-2 text-sm text-navy/70">
          <li>
            ▸ Acuity public URL:{" "}
            <a
              href={BRAND.acuityUrl}
              target="_blank"
              rel="noreferrer"
              className="text-gold hover:underline"
              data-testid="acuity-link"
            >
              {BRAND.acuityUrl}
            </a>
          </li>
          <li>
            ▸ Jotform intake URL:{" "}
            <a
              href={BRAND.jotformUrl}
              target="_blank"
              rel="noreferrer"
              className="text-gold hover:underline"
              data-testid="jotform-link"
            >
              {BRAND.jotformUrl}
            </a>
          </li>
          <li>
            ▸ CDM signup URL:{" "}
            <a
              href={BRAND.cdmSignupUrl}
              target="_blank"
              rel="noreferrer"
              className="text-gold hover:underline"
              data-testid="cdm-link"
            >
              Anew CDM signup
            </a>
          </li>
          <li>
            ▸ Bookipi dashboard:{" "}
            <a
              href={BRAND.bookipiUrl}
              target="_blank"
              rel="noreferrer"
              className="text-gold hover:underline"
              data-testid="bookipi-link"
            >
              {BRAND.bookipiUrl}
            </a>
          </li>
        </ul>
        <p className="mt-4 text-xs text-navy/55 leading-relaxed">
          To change Acuity, Jotform, CDM, or Bookipi URLs, edit{" "}
          <code className="font-mono text-navy/75">frontend/src/lib/data.js</code>{" "}
          → <code className="font-mono text-navy/75">BRAND</code>.
        </p>
      </div>
    </div>
  );
}
